<template>
  <div class="board-settings">
    <h4 class="title is-6 mb-3">Board Settings</h4>

    <div class="settings-field">
      <b-switch
        v-model="isWhiteActive"
        passive-type="is-dark"
        type="is-info"
        size="is-small"
        @input="toggleActiveColor"
      >
        {{ fenData.activeColor === "w" ? "White" : "Black" }} to move
      </b-switch>
      <b-tooltip
        :label="(this.fenError + ' ' + this.fen) | wrappableFen"
        :type="isValidFen ? 'is-success' : 'is-danger'"
        position="is-bottom"
        multilined
      >
        <b-tag
          :type="isValidFen ? 'is-success' : 'is-danger'"
          size="is-small"
          rounded
        >
          {{ isValidFen ? "Valid" : "Invalid" }} FEN
        </b-tag>
      </b-tooltip>
    </div>

    <div class="settings-field">
      <b-switch
        v-model="castlingRights.Q"
        type="is-info"
        size="is-small"
        @input="updateCastlingRight('Q', $event)"
        :disabled="!isWhiteKingOnE1"
      >
        White 0-0-0
      </b-switch>
      <b-switch
        v-model="castlingRights.K"
        type="is-info"
        size="is-small"
        :left-label="true"
        @input="updateCastlingRight('K', $event)"
        :disabled="!isWhiteKingOnE1"
      >
        White 0-0
      </b-switch>
    </div>

    <div class="settings-field">
      <b-switch
        v-model="castlingRights.q"
        type="is-dark"
        size="is-small"
        @input="updateCastlingRight('q', $event)"
        :disabled="!isBlackKingOnE8"
      >
        Black 0-0-0
      </b-switch>
      <b-switch
        v-model="castlingRights.k"
        type="is-dark"
        size="is-small"
        :left-label="true"
        @input="updateCastlingRight('k', $event)"
        :disabled="!isBlackKingOnE8"
      >
        Black 0-0
      </b-switch>
    </div>

    <div horizontal class="settings-field">
      <div class="is-flex is-align-items-center">
        <span class="is-size-7 pr-2">En Passant Target</span>
        <b-select
          v-model="enPassantTarget"
          size="is-small"
          @input="updateEnPassantTarget"
        >
          <option value="-">None</option>
          <option
            v-for="square in enPassantSquares"
            :key="square"
            :value="square"
          >
            {{ square }}
          </option>
        </b-select>
      </div>
    </div>

    <div class="settings-field">
      <div class="fex is-flex-direction-column">
        <span class="is-size-7">Halfmove</span>
        <b-numberinput
          v-model="halfmoveClock"
          size="is-small"
          min="0"
          max="100"
          controls-position="compact"
          @input="updateHalfmoveClock"
        ></b-numberinput>
      </div>

      <div class="fex is-flex-direction-column">
        <span class="is-size-7">Fullmove</span>
        <b-numberinput
          v-model="fullmoveNumber"
          size="is-small"
          min="1"
          max="999"
          controls-position="compact"
          @input="updateFullmoveNumber"
        ></b-numberinput>
      </div>
    </div>

    <!-- <div class="settings-field">
      <b-button type="is-primary" size="is-small" @click="updateFen" expanded>
        Update Position
      </b-button>
    </div> -->
  </div>
</template>

<script>
import Chess from "chess.js";
import { FenData } from "vue-chess-shared/pgn/FenData";

export default {
  name: "BoardSettings",

  props: {
    fen: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      chess: new Chess(),
      fenData: new FenData(this.chess),
      enPassantSquares: FenData.VALID_EN_PASSANT_TARGETS,
      // Local copies of FenData properties for better reactivity
      isWhiteActive: true,
      castlingRights: { K: false, Q: false, k: false, q: false },
      enPassantTarget: "-",
      halfmoveClock: 0,
      fullmoveNumber: 1,
    };
  },

  computed: {
    // Computed property to get current FEN from fenData
    currentFen() {
      if (!this.fenData) return "";
      return this.fenData.fen;
    },

    isWhiteKingOnE1() {
      if (!this.fenData) return false;
      return this.fenData.isWhiteKingOnE1();
    },

    isBlackKingOnE8() {
      if (!this.fenData) return false;
      return this.fenData.isBlackKingOnE8();
    },

    isValidFen() {
      if (!this.fenData) return false;
      return this.fenData.isValid;
    },

    fenError() {
      if (!this.fenData) return "";
      return this.fenData.error;
    },
  },

  watch: {
    fen: {
      immediate: true,
      handler(newFen) {
        this.parseFen(newFen);
        this.syncLocalState();
      },
    },
  },

  methods: {
    parseFen(fen) {
      if (!fen) return;
      this.fenData = new FenData(fen, this.chess);
    },

    // Sync local state variables with fenData for better reactivity
    syncLocalState() {
      this.isWhiteActive = this.fenData.activeColor === "w";
      this.castlingRights = { ...this.fenData.castlingRights };
      this.enPassantTarget = this.fenData.enPassantTarget;
      this.halfmoveClock = this.fenData.halfmoveClock;
      this.fullmoveNumber = this.fenData.fullmoveNumber;
    },

    toggleActiveColor(value) {
      this.fenData.activeColor = value ? "w" : "b";
      this.updateFen();
    },

    updateCastlingRight(right, value) {
      this.fenData.updateCastlingRight(right, value);
      this.updateFen();
    },

    updateEnPassantTarget(value) {
      this.fenData.enPassantTarget = value;
      this.updateFen();
    },

    updateHalfmoveClock(value) {
      this.fenData.halfmoveClock = value;
      this.updateFen();
    },

    updateFullmoveNumber(value) {
      this.fenData.fullmoveNumber = value;
      this.updateFen();
    },

    updateFen() {
      // Create a new FEN with the current piece placement but updated settings
      this.fenData.activeColor = this.isWhiteActive ? "w" : "b";
      this.fenData.castlingRights = this.castlingRights;
      this.fenData.enPassantTarget = this.enPassantTarget;
      this.fenData.halfmoveClock = this.halfmoveClock;
      this.fenData.fullmoveNumber = this.fullmoveNumber;

      this.$emit("fenUpdated", this.fenData.fen);
    },
  },
};
</script>

<style scoped>
.board-settings {
  flex-grow: 0.5;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  min-height: 275px;
  max-height: 350px;
  padding: 0.25rem;
  padding-top: 0.75rem;
  border-top: 1px solid #dbdbdb;
}

.settings-field {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  padding: 0 0.5rem;
  width: 100%;
}
</style>
